import { useEffect, useState, type ReactNode } from "react";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { Button } from "@/components/button";
import { Icon } from "@/components/icons";
import { Tag } from "@/components/tag";
import { useToast } from "@/components/action-feedback";
import { SectionHeader } from "@/app/(app)/settings/_components/section-header";
import { GoogleIcon, GitHubIcon, MicrosoftIcon, OktaIcon } from "@/components/sso-buttons";
import {
  listSocialApps,
  listSocialAccounts,
  deleteSocialAccount,
  type SocialApp,
  type SocialAccount,
} from "@/src/services/sso_service";
import { adaptApiInfo, getInfo } from "@/src/services/info_service";

function providerSlug(name: string): string {
  return name.replace(/[()]/g, "").trim().replace(/\s+/g, "_").toLowerCase();
}

type ProviderMeta = {
  icon: ReactNode;
  description: string;
  badge?: string;
};

const PROVIDER_META: Record<string, ProviderMeta> = {
  google: { icon: <GoogleIcon />, description: "Sign in with Google Workspace." },
  microsoft: {
    icon: <MicrosoftIcon />,
    description: "Sign in with Microsoft Entra ID / Azure AD.",
  },
  okta: { icon: <OktaIcon />, description: "SAML SSO with Okta." },
  github: { icon: <GitHubIcon />, description: "Sign in with GitHub OAuth." },
  google_enterprise: {
    icon: <GoogleIcon />,
    description: "Sign in with you own Google Workspace application.",
  },
  okta_enterprise: { icon: <OktaIcon />, description: "SAML SSO with your Okta tenant." },
};

const PROVIDER_ORDER = Object.keys(PROVIDER_META);

function providerMeta(name: string): ProviderMeta | undefined {
  return PROVIDER_META[providerSlug(name)];
}

function providerRank(name: string): number {
  const index = PROVIDER_ORDER.indexOf(providerSlug(name));
  return index === -1 ? PROVIDER_ORDER.length : index;
}

function ProviderIcon({ app }: { app: SocialApp }) {
  const meta = providerMeta(app.name);
  const [failed, setFailed] = useState(false);

  if (meta) return <>{meta.icon}</>;
  if (!app.icon || failed) return <Icon name="key" size={24} />;
  return (
    <img src={app.icon} alt="" className="size-6 object-contain" onError={() => setFailed(true)} />
  );
}

type LinkedAccountsStatus = "loading" | "error" | "ready";

export function LinkedAccounts({
  children,
  title = "Linked accounts",
  description = "Connect a single sign-on provider to log in without a password.",
  fill = false,
}: {
  children?: ReactNode;
  title?: string;
  description?: string;
  fill?: boolean;
}) {
  const [status, setStatus] = useState<LinkedAccountsStatus>("loading");
  const [apps, setApps] = useState<SocialApp[]>([]);
  const [accounts, setAccounts] = useState<SocialAccount[]>([]);
  const [enforceSso, setEnforceSso] = useState(false);
  const [pendingDisconnect, setPendingDisconnect] = useState<SocialAccount | null>(null);
  const [disconnecting, setDisconnecting] = useState(false);
  const [disconnectError, setDisconnectError] = useState<string | null>(null);
  const showToast = useToast();

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const [appsRes, accountsRes, infoRes] = await Promise.all([
        listSocialApps(),
        listSocialAccounts(),
        getInfo()
          .then(adaptApiInfo)
          .then(
            (info) => info.company.enforceSso,
            () => false,
          ),
      ]);
      if (cancelled) return;
      if (!appsRes.ok || !accountsRes.ok) {
        setStatus("error");
        return;
      }
      setApps(appsRes.value);
      setAccounts(accountsRes.value);
      setEnforceSso(infoRes);
      setStatus("ready");
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const sortedApps = [...apps].sort((a, b) => providerRank(a.name) - providerRank(b.name));

  const isLastWithEnforce = enforceSso && accounts.length === 1;

  async function confirmDisconnect() {
    const target = pendingDisconnect;
    if (!target || isLastWithEnforce) return;
    setDisconnecting(true);
    setDisconnectError(null);
    const res = await deleteSocialAccount(target.id);
    if (!res.ok) {
      setDisconnecting(false);
      setDisconnectError(res.error.message);
      return;
    }
    setAccounts((prev) => prev.filter((a) => a.id !== target.id));
    setDisconnecting(false);
    setPendingDisconnect(null);
    showToast({ tone: "success", text: "Account disconnected successfully." });
  }

  if (status === "loading") return <SkeletonCard heightClass="h-[100px]" />;

  return (
    <div className="card @container flex flex-col gap-4">
      <SectionHeader title={title} description={description} />

      {children}

      {status === "error" ? (
        <p className="text-small text-grey-900">
          Couldn&apos;t load your linked accounts. Refresh the page to try again.
        </p>
      ) : sortedApps.length === 0 ? (
        <p className="text-small text-grey-900">No single sign-on providers are available yet.</p>
      ) : (
        <div className="flex flex-wrap gap-3">
          {sortedApps.map((app) => {
            const account = accounts.find((a) => a.provider.name === app.name);
            const connected = !!account;
            const meta = providerMeta(app.name);
            const description = meta?.description ?? `Sign in with ${app.name}.`;

            return (
              <div
                key={app.name}
                className={[
                  "flex flex-col justify-between gap-3 rounded-sm border p-4",
                  fill ? "min-w-44 flex-1" : "aspect-square w-44",
                  connected
                    ? "border-green-500 bg-white shadow-flat-green"
                    : "border-grey-200 bg-white shadow-flat",
                ].join(" ")}
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="flex size-10 shrink-0 items-center justify-center rounded-sm border border-grey-200">
                    <ProviderIcon app={app} />
                  </span>
                  {connected ? (
                    <Tag tone="positive" leadingIcon={<Icon name="check" size={20} />}>
                      Connected
                    </Tag>
                  ) : (
                    meta?.badge && <Tag tone="neutral">{meta.badge}</Tag>
                  )}
                </div>

                <div className="flex flex-col gap-0.5">
                  <span className="font-medium text-black">{app.name}</span>
                  <p className="text-small text-grey-900">{description}</p>
                </div>

                {connected ? (
                  <Button
                    hierarchy="secondary"
                    size="sm"
                    className="w-full"
                    aria-label={`Disconnect ${app.name}`}
                    onClick={() => {
                      setDisconnectError(null);
                      setPendingDisconnect(account);
                    }}
                  >
                    Disconnect
                  </Button>
                ) : (
                  <Button
                    hierarchy="secondary"
                    size="sm"
                    className="w-full"
                    aria-label={`Connect ${app.name}`}
                    href={app.connectUrl}
                  >
                    Connect
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <ConfirmDialog
        open={pendingDisconnect !== null}
        onClose={() => setPendingDisconnect(null)}
        onConfirm={() => void confirmDisconnect()}
        title={
          pendingDisconnect
            ? `Disconnect ${pendingDisconnect.provider.name}?`
            : "Disconnect account?"
        }
        description={
          isLastWithEnforce
            ? "Your company has SSO enforcement enabled. You cannot disconnect your last SSO provider."
            : "You'll no longer be able to sign in with this provider."
        }
        confirmLabel="Disconnect"
        confirmDisabled={isLastWithEnforce}
        busyLabel="Disconnecting…"
        busy={disconnecting}
        error={disconnectError ?? undefined}
        destructive
      />
    </div>
  );
}

function SkeletonCard({ heightClass }: { heightClass: string }) {
  return (
    <div className={`card flex animate-pulse flex-col gap-3 ${heightClass}`} aria-hidden>
      <div className="h-5 w-1/3 rounded bg-grey-100" />
      <div className="h-3 w-2/3 rounded bg-grey-100" />
      <div className="flex-1" />
    </div>
  );
}
